/**
 * ADR-0035 Amendment 9: "Not applicable on this shift" — the planner's read.
 *
 * A carer may answer a plan item the planner marked `_allowNotApplicable`
 * with a STRUCTURED ABSENCE: an ordinary monitored-event `_observation` with
 * no value[x] and
 *
 *   dataAbsentReason.coding ∋ {system: DATA_ABSENT_REASON_SYSTEM, code: "not-applicable"}
 *                           + {system: NOT_APPLICABLE_REASON_SYSTEM, code: day-shift | not-in-care | other}
 *   dataAbsentReason.text   = the reason's label
 *   note[0].text            = optional (required by the app for `other`)
 *
 * The planner's three rules for it, one place each:
 *   - narrate it as "Not applicable — {text}" (+ note), muted, never as a value
 *     (narrative.ts, monitored-chart.ts diary timeline);
 *   - never plot it: no chart, trend, count or window edge (monitored-chart-data
 *     `plottableRecordings`);
 *   - it ANSWERS the item: counted as a recording wherever the planner counts
 *     them (narrative day/week totals).
 *
 * The predicate mirrors app.dsr `is_not_applicable` (dsr/app_evidence.py) and
 * the carer app's own: the data-absent-reason coding is the whole test. It
 * deliberately does not re-check the plan's `_allowNotApplicable` (am.9c: a
 * later planner change does not retroactively un-answer a shift).
 *
 * Pure, no imports — Node-testable.
 */

export const DATA_ABSENT_REASON_SYSTEM = "http://terminology.hl7.org/CodeSystem/data-absent-reason";
export const NOT_APPLICABLE_CODE = "not-applicable";
export const NOT_APPLICABLE_REASON_SYSTEM = "https://sweettreeconnect.com/fhir/CodeSystem/not-applicable-reason";

/** Labels for the reason codes, used only when `dataAbsentReason.text` is missing. */
const REASON_LABELS: Record<string, string> = {
  "day-shift": "Day shift",
  "not-in-care": "Not in my care during this",
  other: "Other",
};

/** The plan editor's checkbox, and its help text (am.9a). */
export const ALLOW_NOT_APPLICABLE_LABEL = "Carers may mark this not applicable";
export const ALLOW_NOT_APPLICABLE_HELP =
  "For items that can't happen on some shifts, e.g. sleep on a day shift. The carer must choose a reason; it closes the item and is shown as not applicable, never as data.";
/** The plan view / print chip for an item that allows it. */
export const ALLOW_NOT_APPLICABLE_CHIP = "Carers may mark N/A";

type Coding = { system?: unknown; code?: unknown; display?: unknown };

function codingsOf(data: Record<string, unknown>): Coding[] {
  const dar = data.dataAbsentReason;
  if (!dar || typeof dar !== "object") return [];
  const coding = (dar as { coding?: unknown }).coding;
  return Array.isArray(coding) ? coding.filter((c): c is Coding => !!c && typeof c === "object") : [];
}

/** Is this Observation a carer's "not applicable on this shift" answer? */
export function isNotApplicable(data: Record<string, unknown>): boolean {
  return codingsOf(data).some((c) => c.system === DATA_ABSENT_REASON_SYSTEM && c.code === NOT_APPLICABLE_CODE);
}

/** The reason code and its label, for an Observation `isNotApplicable` accepts. */
export function notApplicableReason(data: Record<string, unknown>): { code?: string; text: string } {
  const reason = codingsOf(data).find((c) => c.system === NOT_APPLICABLE_REASON_SYSTEM);
  const code = typeof reason?.code === "string" ? reason.code : undefined;
  const dar = data.dataAbsentReason as { text?: unknown } | undefined;
  const text = (typeof dar?.text === "string" && dar.text.trim())
    || (typeof reason?.display === "string" && reason.display.trim())
    || (code ? REASON_LABELS[code] ?? code : "")
    || "no reason given";
  return { code, text };
}

/** "Not applicable — Day shift": the one wording every planner surface uses. */
export function notApplicableLabel(text: string | undefined): string {
  return `Not applicable — ${text && text.trim() ? text.trim() : "no reason given"}`;
}
